import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { CustomSwitch } from "components/shared/custom-switch";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { neutral, primary } from "style/color";
import { ReactComponent as EyeOpen } from "./../../../assets/Icons/eye-open.svg";
import {
  CashInfo,
  GRANT_REQUEST_TRANSACTION_TYPE_ENUM,
  GrantRequestInput,
  InvestmentInfo,
} from "types/grant-request";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Charity } from "types/charity";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import {
  compileHtmlToPDF,
  generateAcknowledgementLetterHtml,
} from "utils/create-pdf";
import { useGetFundQuery } from "hooks/use-services/fund.service";
import awsExports from "aws-exports";

export interface DonorSelection {
  memberId: number;
  memberName: string;
  address: string;
  recognition: boolean;
  recommendation: boolean;
  recognitionWithAddress: boolean;
  isCustom: boolean;
}

interface GrantAcknowledgementDetailsProps {
  grantRequest: GrantRequestInput;
  charity: Charity;
  handleGrantRequestChange: (key: string, value: any) => void;
}

export const GrantAcknowledgementDetails: FC<
  GrantAcknowledgementDetailsProps
> = ({ grantRequest, charity, handleGrantRequestChange }) => {
  const { selectedAccount, userData } = useContext<AuthData>(AuthContext);
  const {
    data: fundAccount,
    isLoading,
    error,
  } = useGetFundQuery(
    selectedAccount?.fundAccountId || 0,
    !!selectedAccount?.fundAccountId // Only run query if we have a valid ID
  );

  const [inHonorOf, setInHonorOf] = useState<boolean>(
    grantRequest.acknowledgement.inHonorOf &&
      grantRequest.acknowledgement.inHonorOf.length > 0
      ? true
      : false
  );
  const [inMemoryOf, setInMemoryOf] = useState<boolean>(
    grantRequest.acknowledgement.inMemoryOf &&
      grantRequest.acknowledgement.inMemoryOf.length > 0
      ? true
      : false
  );

  // New state for fund name recognition and recommendation
  const [fundNameRecognition, setFundNameRecognition] =
    useState<boolean>(false);
  const [fundNameRecommendation, setFundNameRecommendation] =
    useState<boolean>(true);

  // Initialize member selections
  const [donorSelections, setDonorSelections] = useState<DonorSelection[]>([]);

  // New state for custom input fields
  const [customRecommendationName, setCustomRecommendationName] =
    useState<string>("");
  const [customRecognitionName, setCustomRecognitionName] =
    useState<string>("");
  const [customRecognitionAddress, setCustomRecognitionAddress] =
    useState<string>("");

  useEffect(() => {
    if (fundAccount?.fundAccountMembers) {
      const newSelections = fundAccount.fundAccountMembers
        .filter(
          (member) => member.user.sub !== awsExports.neta_super_admin_user_sub
        )
        .map((member) => ({
          memberId: member.user.userId ?? 0,
          memberName: `${member.user.firstName || ""} ${
            member.user.lastName || ""
          }`.trim(),
          address: `${member.user?.addressLine1}${
            member.user?.addressLine2 ? `, ${member.user?.addressLine2},` : `, `
          } ${member.user?.city}, ${member.user?.state}, ${member.user?.zip}`,
          recognition: true,
          recommendation: false,
          recognitionWithAddress: false,
          isCustom: false,
        }));
      setDonorSelections(newSelections);
    }
  }, [fundAccount?.fundAccountMembers]);

  // Use effect to update combined lists when selections change
  useEffect(() => {
    updateCombinedLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donorSelections]);

  const handleAnonymousChange = useCallback(
    (checked: boolean) => {
      if (!checked) {
        // When anonymous is turned OFF, update all related fields at once
        handleAcknowledgementChange("anonymous", false);
        handleAcknowledgementChange("showFundNameInRecommendation", true);
        handleAcknowledgementChange("showFundNameInRecognition", true);
        setFundNameRecognition(true);
        setFundNameRecommendation(true);
      } else {
        // When anonymous is turned ON, just update anonymous
        handleAcknowledgementChange("anonymous", true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      grantRequest.acknowledgement.anonymous,
      setFundNameRecognition,
      setFundNameRecommendation,
    ]
  );

  // In your JSX:
  <CustomSwitch
    onChange={(event) => {
      handleAnonymousChange(event.target.checked);
    }}
    checked={grantRequest.acknowledgement.anonymous}
  />;

  const openPdf = () => {
    const amount =
      grantRequest.transactionType === GRANT_REQUEST_TRANSACTION_TYPE_ENUM.CASH
        ? (grantRequest.information as CashInfo).requestedAmount
        : (grantRequest.information as InvestmentInfo).requestedAmount;

    compileHtmlToPDF(
      generateAcknowledgementLetterHtml({
        acknowledgement: grantRequest.acknowledgement,
        fund: selectedAccount!,
        user: userData?.user!,
        charity: charity,
        amount: amount,
        purpose: grantRequest.grantPurpose,
        personToInform: grantRequest.toInform,
      })
    );
  };

  const handleAcknowledgementChange = (key: string, value: any) => {
    handleGrantRequestChange("acknowledgement", {
      ...grantRequest.acknowledgement,
      [key]: value,
    });
  };

  const handleMemberSelectionChange = (
    memberId: number,
    type: "recognition" | "recommendation" | "recognitionWithAddress",
    checked: boolean
  ) => {
    setDonorSelections((prev) => {
      return prev.map((member) =>
        member.memberId === memberId ? { ...member, [type]: checked } : member
      );
    });
  };

  const updateCombinedLists = () => {
    // Get selected members for recognition and recommendation
    const recognitionMembers = donorSelections
      .filter((m) => m.recognition)
      .map((m) => m.memberName);
    const recommendationMembers = donorSelections
      .filter((m) => m.recommendation)
      .map((m) => m.memberName);
    const recognitionWithAddressMembers = donorSelections
      .filter((m) => m.recognitionWithAddress)
      .map((m) => m.memberName);

    // Update acknowledgement data
    handleAcknowledgementChange(
      "selectedMembersRecognition",
      recognitionMembers
    );
    handleAcknowledgementChange(
      "selectedMembersRecommendation",
      recommendationMembers
    );
    handleAcknowledgementChange(
      "selectedMembersRecognitionWithAddress",
      recognitionWithAddressMembers
    );
    handleAcknowledgementChange("donorSelections", donorSelections);
  };

  const handleCustomRecommendationChange = (value: string) => {
    setCustomRecommendationName(value);

    if (value.trim()) {
      // Add or update custom recommendation entry
      setDonorSelections((prev) => {
        // Remove existing custom recommendation entry
        const filteredSelections = prev.filter(
          (member) =>
            !(
              member.isCustom &&
              member.recommendation &&
              !member.recognition &&
              !member.recognitionWithAddress
            )
        );

        // Add new custom recommendation entry
        const customEntry: DonorSelection = {
          memberId: -1, // Use negative ID for custom entries
          memberName: value.trim(),
          address: "",
          recognition: false,
          recommendation: true,
          recognitionWithAddress: false,
          isCustom: true,
        };

        return [...filteredSelections, customEntry];
      });
    } else {
      // Remove custom recommendation entry if input is empty
      setDonorSelections((prev) =>
        prev.filter(
          (member) =>
            !(
              member.isCustom &&
              member.recommendation &&
              !member.recognition &&
              !member.recognitionWithAddress
            )
        )
      );
    }
  };

  const handleCustomRecognitionChange = (
    nameValue: string,
    addressValue: string
  ) => {
    setCustomRecognitionName(nameValue);
    setCustomRecognitionAddress(addressValue);

    if (nameValue.trim()) {
      // Add or update custom recognition entry
      setDonorSelections((prev) => {
        // Remove existing custom recognition entry
        const filteredSelections = prev.filter(
          (member) =>
            !(
              member.isCustom &&
              (member.recognition || member.recognitionWithAddress) &&
              !member.recommendation
            )
        );

        // Determine which type of recognition based on address
        const hasAddress = addressValue.trim() !== "";

        const customEntry: DonorSelection = {
          memberId: -2, // Use different negative ID for recognition entries
          memberName: nameValue.trim(),
          address: addressValue.trim(),
          recognition: !hasAddress, // Only recognition if no address
          recommendation: false,
          recognitionWithAddress: hasAddress, // Recognition with address if address provided
          isCustom: true,
        };

        return [...filteredSelections, customEntry];
      });
    } else {
      // Remove custom recognition entry if name input is empty
      setDonorSelections((prev) =>
        prev.filter(
          (member) =>
            !(
              member.isCustom &&
              (member.recognition || member.recognitionWithAddress) &&
              !member.recommendation
            )
        )
      );
    }
  };

  const handleChange = (index: number, value: string) => {
    const emails = grantRequest.acknowledgement.thirdPartiesEmails;
    emails[index] = value;
    handleGrantRequestChange("acknowledgement", {
      ...grantRequest.acknowledgement,
      thirdPartiesEmails: emails,
    });
  };

  const removeEmail = (index: number) => {
    const emails = grantRequest.acknowledgement.thirdPartiesEmails;
    emails.splice(index, 1);
    handleGrantRequestChange("acknowledgement", {
      ...grantRequest.acknowledgement,
      thirdPartiesEmails: emails,
    });
  };

  return (
    <Grid container rowSpacing={0} columnSpacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle3" color={neutral[900]}>
          Acknowledgement details to show in the grant letter*
        </Typography>
        <Typography
          variant="bodyCopyRegular"
          color={neutral[600]}
          sx={{ mb: 0 }}
          paragraph
        >
          Please use the below to customize the grant letter we send out with
          the grant
        </Typography>
      </Grid>
      <Grid sx={{ mt: 1 }} item xs={12}>
        <FormControlLabel
          control={
            <CustomSwitch
              onChange={(event) => {
                const isAnonymous = event.target.checked;

                // Single state update with all changes at once
                const newAcknowledgement = {
                  ...grantRequest.acknowledgement,
                  anonymous: isAnonymous,
                  ...(isAnonymous
                    ? {} // When turning ON anonymous, don't change other fields
                    : {
                        // When turning OFF anonymous, enable fund name fields
                        showFundNameInRecommendation: true,
                        showFundNameInRecognition: true,
                      }),
                };

                // Update the main state once
                handleGrantRequestChange("acknowledgement", newAcknowledgement);

                // Update local state without triggering additional renders
                if (!isAnonymous) {
                  // When turning OFF anonymous (setting to false), restore defaults
                  setFundNameRecommendation(true);
                  setDonorSelections((prev) =>
                    prev.map((member) => ({
                      ...member,
                      recognition: true,
                      recommendation: false,
                      recognitionWithAddress: false,
                    }))
                  );
                } else {
                  // When turning ON anonymous, uncheck all member selections
                  setFundNameRecognition(false);
                  setFundNameRecommendation(false);
                  setDonorSelections((prev) =>
                    prev.map((member) => ({
                      ...member,
                      recognition: false,
                      recommendation: false,
                      recognitionWithAddress: false,
                    }))
                  );
                }
              }}
              checked={grantRequest.acknowledgement.anonymous}
            />
          }
          label="I want the grant to be anonymous"
        />
      </Grid>
      {!grantRequest.acknowledgement.anonymous && (
        <>
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Typography variant="subtitle3" color={neutral[900]}>
              Member Recognition & Recommendation
            </Typography>
            <Typography
              variant="bodyCopyRegular"
              color={neutral[600]}
              sx={{ mb: 2 }}
              paragraph
            >
              Select to include in recognition and/or recommendation
            </Typography>
          </Grid>

          <Grid
            container
            alignItems="flex-start"
            sx={{
              mb: 2,
              ml: 2,
              px: 0.5,
              pt: 1,
            }}
          >
            <Grid item xs={12} sm={6} md={5}>
              <Typography
                variant="bodyCopyBold"
                sx={{
                  mb: 0.5,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  width: "100%",
                }}
              >
                Show your fund's name
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3} md={2.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fundNameRecommendation}
                    onChange={(event) => {
                      setFundNameRecommendation(event.target.checked);
                      handleAcknowledgementChange(
                        "showFundNameInRecommendation",
                        event.target.checked
                      );
                    }}
                  />
                }
                label="Recommendation"
                sx={{
                  width: "100%",
                  "& .MuiFormControlLabel-label": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    lineHeight: 1.2,
                  },
                  margin: 0,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fundNameRecognition}
                    onChange={(event) => {
                      setFundNameRecognition(event.target.checked);
                      handleAcknowledgementChange(
                        "showFundNameInRecognition",
                        event.target.checked
                      );
                    }}
                  />
                }
                label="Recognition"
                sx={{
                  width: "100%",
                  "& .MuiFormControlLabel-label": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    lineHeight: 1.2,
                  },
                  margin: 0,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={2}></Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="bodyCopyRegular" sx={{ fontWeight: "bold" }}>
              Show donor name & address
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            {donorSelections.map((member) => (
              <Grid
                key={`${member.memberId}-${
                  member.isCustom ? "custom" : "regular"
                }`}
                container
                alignItems="flex-start"
                sx={{
                  mb: 2,
                  px: 0.5,
                  py: 0.5,
                }}
              >
                <Grid item xs={12} sm={6} md={5}>
                  <Typography
                    variant="bodyCopyRegular"
                    sx={{
                      mb: 0.5,
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      width: "100%",
                      fontWeight: "500",
                    }}
                  >
                    {member.memberName} {member.isCustom && "(Custom)"}
                  </Typography>
                  {member.address && (
                    <Typography
                      variant="caption"
                      color={neutral[600]}
                      sx={{
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                        width: "100%",
                        display: "block",
                        mt: 0.5,
                      }}
                    >
                      {member.address}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={6} sm={2} md={2.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={member.recommendation}
                        onChange={(event) =>
                          handleMemberSelectionChange(
                            member.memberId,
                            "recommendation",
                            event.target.checked
                          )
                        }
                        disabled={member.isCustom}
                        size="small"
                      />
                    }
                    label="Recommendation"
                    sx={{
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        lineHeight: 1.2,
                      },
                      margin: 0,
                    }}
                  />
                </Grid>

                <Grid item xs={6} sm={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={member.recognition}
                        onChange={(event) =>
                          handleMemberSelectionChange(
                            member.memberId,
                            "recognition",
                            event.target.checked
                          )
                        }
                        disabled={
                          member.isCustom || member.recognitionWithAddress
                        }
                        size="small"
                      />
                    }
                    label="Recognition"
                    sx={{
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        lineHeight: 1.2,
                      },
                      margin: 0,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={2} md={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={member.recognitionWithAddress || false}
                        onChange={(event) =>
                          handleMemberSelectionChange(
                            member.memberId,
                            "recognitionWithAddress",
                            event.target.checked
                          )
                        }
                        disabled={member.isCustom || member.recognition}
                        size="small"
                      />
                    }
                    label="Recognition with Address"
                    sx={{
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        lineHeight: 1.2,
                        whiteSpace: { xs: "normal", sm: "nowrap" }, // Allow wrapping on mobile
                      },
                      margin: 0,
                    }}
                  />
                </Grid>
              </Grid>
            ))}

            <Box sx={{ mt: 3 }}>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography
                    variant="bodyCopyRegular"
                    sx={{ fontWeight: "bold", pr: 1 }}
                  >
                    Add Custom Recommendation Name
                  </Typography>
                  <Typography
                    variant="bodyCopyRegular"
                    color={neutral[600]}
                    sx={{ pr: 1 }}
                  >
                    (optional)
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  placeholder="Enter name for recommendation"
                  value={customRecommendationName}
                  onChange={(event) =>
                    handleCustomRecommendationChange(event.target.value)
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography
                    variant="bodyCopyRegular"
                    sx={{ fontWeight: "bold", pr: 1 }}
                  >
                    Add Custom Recognition Name & Address
                  </Typography>
                  <Typography
                    variant="bodyCopyRegular"
                    color={neutral[600]}
                    sx={{ pr: 1 }}
                  >
                    (optional)
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  placeholder="Enter name for recognition"
                  value={customRecognitionName}
                  onChange={(event) =>
                    handleCustomRecognitionChange(
                      event.target.value,
                      customRecognitionAddress
                    )
                  }
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter address for recognition"
                  value={customRecognitionAddress}
                  onChange={(event) =>
                    handleCustomRecognitionChange(
                      customRecognitionName,
                      event.target.value
                    )
                  }
                  size="small"
                />
              </Grid>
            </Box>
          </Grid>
          {isLoading && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography>Loading members...</Typography>
            </Grid>
          )}
          {error && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography color="error">Error loading members </Typography>
            </Grid>
          )}
          {!isLoading &&
            donorSelections.filter((m) => !m.isCustom).length === 0 && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography>No members found for this fund account.</Typography>
              </Grid>
            )}
        </>
      )}

      <Grid item xs={12} mt={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={inHonorOf}
              onChange={(event) => {
                setInHonorOf(event.target.checked);
                if (!event.target.checked) {
                  handleAcknowledgementChange("inHonorOf", "");
                }
              }}
              inputProps={{ "aria-label": "controlled" }}
            />
          }
          label="Add In honour of"
          name="reuse"
        />
      </Grid>
      <Grid sx={{ ml: 3 }} item xs={8}>
        <FormControl fullWidth>
          <TextField
            disabled={!inHonorOf}
            required={inHonorOf}
            multiline
            rows={4}
            value={grantRequest.acknowledgement.inHonorOf}
            onChange={(event) =>
              handleAcknowledgementChange("inHonorOf", event.target.value)
            }
            placeholder="Enter name and/or address"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              onChange={(event) => {
                setInMemoryOf(event.target.checked);
                if (!event.target.checked) {
                  handleAcknowledgementChange("inMemoryOf", "");
                }
              }}
              checked={inMemoryOf}
              inputProps={{ "aria-label": "controlled" }}
            />
          }
          label="Add In memory of"
          name="addInMemoryOf"
        />
      </Grid>
      <Grid sx={{ ml: 3 }} item xs={8}>
        <FormControl fullWidth>
          <TextField
            disabled={!inMemoryOf}
            required={inMemoryOf}
            multiline
            rows={4}
            value={grantRequest.acknowledgement.inMemoryOf}
            onChange={(event) =>
              handleAcknowledgementChange("inMemoryOf", event.target.value)
            }
            placeholder="Enter name and/or address"
          />
        </FormControl>
      </Grid>
      <Grid sx={{ my: 4 }} item xs={8}>
        <Button
          size="medium"
          variant="outlined"
          startIcon={<EyeOpen />}
          onClick={openPdf}
        >
          See letter preview
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle3" color={neutral[900]}>
          Send to third parties
        </Typography>
        <Typography
          variant="bodyCopyRegular"
          color={neutral[600]}
          sx={{ mb: 0 }}
          paragraph
        >
          The charity receiving the grant will be notified by a standard
          communication. <br></br>If you wish to notify other parties of this
          grant, add their email(s) below and they will get a notification.{" "}
        </Typography>
      </Grid>
      {grantRequest.acknowledgement.thirdPartiesEmails.map((email, index) => (
        <>
          <Grid sx={{ mt: 2 }} item xs={8} sm={8}>
            <FormControl fullWidth>
              <TextField
                size="small"
                fullWidth
                name="email"
                value={email}
                onChange={(event) => handleChange(index, event.target.value)}
                variant="outlined"
                type="text"
                placeholder="Enter email"
              />
            </FormControl>
          </Grid>
          {grantRequest.acknowledgement.thirdPartiesEmails.length !== 1 && (
            <Grid sx={{ mt: 2 }} item xs={1} sm={1}>
              <IconButton
                onClick={(event) => removeEmail(index)}
                sx={{
                  backgroundColor: "#fff",
                  color: primary[500],
                  border: "1px solid #0B3A81",
                  "&:hover": {
                    backgroundColor: primary[500],
                    color: "#fff",
                  },
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Grid>
          )}
        </>
      ))}
      <Grid sx={{ mt: 2 }} item xs={1} sm={1}>
        <IconButton
          onClick={(event) =>
            handleChange(
              grantRequest.acknowledgement.thirdPartiesEmails.length,
              ""
            )
          }
          sx={{
            backgroundColor: "#fff",
            color: primary[500],
            border: "1px solid #0B3A81",
            "&:hover": {
              backgroundColor: primary[500],
              color: "#fff",
            },
          }}
          disabled={
            grantRequest.acknowledgement.thirdPartiesEmails[
              grantRequest.acknowledgement.thirdPartiesEmails.length - 1
            ] === ""
          }
        >
          <AddIcon />
        </IconButton>
      </Grid>
      {!grantRequest.acknowledgement.anonymous && (
        <>
          <Grid sx={{ mt: 2 }} item xs={12}>
            <Typography variant="subtitle3" color={neutral[900]}>
              Visibility*
            </Typography>
            <Typography
              variant="bodyCopyRegular"
              color={neutral[600]}
              sx={{ mb: 0 }}
              paragraph
            >
              We do not control how the recipient of the grant will publicly
              recognize you (or not), but we can tell them your preference{" "}
            </Typography>
          </Grid>
          <Grid sx={{ mt: 1 }} item xs={12}>
            <RadioGroup
              onChange={(event) =>
                handleAcknowledgementChange(
                  "visibility",
                  event.target.value === "true"
                )
              }
              value={grantRequest.acknowledgement.visibility}
              name="visibility"
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="Yes, I agree to have the acknowledgement details indicated above shared publicly by the recipient"
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="No, I prefer to not have my details shared publicly"
              />
            </RadioGroup>
          </Grid>
        </>
      )}
    </Grid>
  );
};
